import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Plus, X, CheckCircle } from "@phosphor-icons/react";
import { useMutation } from "@tanstack/react-query";
import { api } from "../api";
import { Input, Select } from "./ui";

type SuggestPayload = {
  name: string;
  url?: string | null;
  city?: string | null;
  country?: string | null;
  discipline?: string | null;
  distance_km?: number | null;
  race_date?: string | null;
  notes?: string | null;
};

export function SuggestMissingEventCTA() {
  const { t } = useTranslation("events");
  const [open, setOpen] = useState(false);

  return (
    <>
      <div className="text-center py-4">
        <button
          type="button"
          onClick={() => setOpen(true)}
          className="text-fg-secondary hover:text-fg-primary text-[12px] inline-flex items-center gap-1.5 transition-colors"
        >
          <Plus className="w-3.5 h-3.5" />
          {t("suggest.cta_button")}
        </button>
      </div>
      {open && <SuggestModal onClose={() => setOpen(false)} />}
    </>
  );
}

function SuggestModal({ onClose }: { onClose: () => void }) {
  const { t } = useTranslation("events");
  const { t: tCommon } = useTranslation("common");
  const [name, setName] = useState("");
  const [url, setUrl] = useState("");
  const [city, setCity] = useState("");
  const [country, setCountry] = useState("");
  const [discipline, setDiscipline] = useState("run");
  const [distance, setDistance] = useState("");
  const [date, setDate] = useState("");
  const [notes, setNotes] = useState("");
  const [done, setDone] = useState(false);

  const mut = useMutation({
    mutationFn: async (body: SuggestPayload) => {
      const r = await api.post("/athlete/events/suggest", body);
      return r.data;
    },
  });

  const submit = () => {
    if (!name.trim()) return;
    const payload: SuggestPayload = {
      name: name.trim(),
      url: url.trim() || null,
      city: city.trim() || null,
      country: country.trim().toUpperCase() || null,
      discipline,
      distance_km: distance ? parseFloat(distance.replace(",", ".")) : null,
      race_date: date || null,
      notes: notes.trim() || null,
    };
    mut.mutate(payload, {
      onSuccess: () => {
        setDone(true);
        setTimeout(onClose, 1500);
      },
    });
  };

  return (
    <div
      className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-end sm:items-center justify-center"
      onClick={onClose}
    >
      <div
        className="w-full sm:max-w-md bg-bg-surface rounded-t-card sm:rounded-card max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="sticky top-0 bg-bg-surface border-b border-bg-divider/50 px-4 py-3 flex items-center justify-between">
          <h2 className="text-base font-semibold">{t("suggest.modal_title")}</h2>
          <button
            onClick={onClose}
            className="p-1 -m-1 text-fg-muted"
            aria-label={tCommon("a11y.close")}
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {done ? (
          <div className="p-6 text-center space-y-3">
            <CheckCircle className="w-12 h-12 text-emerald-400 mx-auto" />
            <div className="text-fg-primary font-medium">{t("suggest.success_title")}</div>
            <div className="text-fg-muted text-[13px] leading-relaxed">
              {t("suggest.success_text")}
            </div>
          </div>
        ) : (
          <div className="p-4 space-y-3">
            <Input
              label={`${t("suggest.label_name")} *`}
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder={t("suggest.placeholder_name")}
            />
            <Input
              label={t("suggest.label_url")}
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              placeholder="https://..."
            />
            <div className="grid grid-cols-2 gap-2">
              <Input
                label={t("suggest.label_city")}
                value={city}
                onChange={(e) => setCity(e.target.value)}
                placeholder={t("suggest.placeholder_city")}
              />
              <Input
                label={t("suggest.label_country")}
                value={country}
                onChange={(e) => setCountry(e.target.value.toUpperCase())}
                maxLength={2}
                placeholder={t("suggest.placeholder_country")}
              />
            </div>
            <div className="grid grid-cols-2 gap-2">
              <Select
                label={t("suggest.label_discipline")}
                value={discipline}
                onChange={(e) => setDiscipline(e.target.value)}
                options={[
                  { value: "run", label: t("suggest.discipline_run") },
                  { value: "trail", label: t("suggest.discipline_trail") },
                  { value: "triathlon", label: t("suggest.discipline_triathlon") },
                  { value: "duathlon", label: t("suggest.discipline_duathlon") },
                  { value: "swim", label: t("suggest.discipline_swim") },
                  { value: "hyrox", label: t("suggest.discipline_hyrox") },
                  { value: "bike", label: t("suggest.discipline_bike") },
                ]}
              />
              <Input
                label={t("suggest.label_distance")}
                inputMode="decimal"
                value={distance}
                onChange={(e) => setDistance(e.target.value)}
                placeholder={t("suggest.placeholder_distance")}
              />
            </div>
            <Input
              label={t("suggest.label_date")}
              type="date"
              value={date}
              onChange={(e) => setDate(e.target.value)}
            />
            <div>
              <label className="text-[11px] text-fg-muted">{t("suggest.label_notes")}</label>
              <textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                rows={2}
                placeholder={tCommon("a11y.event_extra")}
                className="w-full mt-1 px-3 py-2 rounded-pill bg-bg-raised border border-bg-border text-sm resize-none"
              />
            </div>
            {mut.isError && (
              <div className="text-[11px] text-rose-300">{t("suggest.error_submit")}</div>
            )}
            <button
              type="button"
              onClick={submit}
              disabled={mut.isPending || !name.trim()}
              className="w-full px-3 py-2 rounded-pill bg-accent text-bg-base font-medium disabled:opacity-50"
            >
              {mut.isPending ? t("suggest.btn_sending") : t("suggest.btn_submit")}
            </button>
            <div className="text-[11px] text-fg-muted text-center leading-relaxed">
              {t("suggest.footer")}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
